import { ArrowDown, ArrowUp, ArrowUpDown, Plus, Trash2 } from 'lucide-react';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { cn } from '@/shared/utils/utils';

export interface ListSortState<TKey extends string> {
  key: TKey;
  direction: 'asc' | 'desc';
}

export interface ListSortOption<TKey extends string> {
  key: TKey;
  label: string;
}

interface ListSortMenuProps<TKey extends string> {
  sort: readonly ListSortState<TKey>[];
  options: readonly ListSortOption<TKey>[];
  onSortChange: (sort: ListSortState<TKey>[]) => void;
  className?: string;
}

export const ListSortMenu = <TKey extends string>({
  sort,
  options,
  onSortChange,
  className,
}: ListSortMenuProps<TKey>) => {
  const updateRule = (index: number, patch: Partial<ListSortState<TKey>>) => {
    onSortChange(
      sort.map((rule, ruleIndex) => (ruleIndex === index ? { ...rule, ...patch } : rule))
    );
  };

  const moveRule = (index: number, offset: -1 | 1) => {
    const targetIndex = index + offset;
    if (targetIndex < 0 || targetIndex >= sort.length) return;

    const next = [...sort];
    [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
    onSortChange(next);
  };

  const removeRule = (index: number) => {
    onSortChange(sort.filter((_, ruleIndex) => ruleIndex !== index));
  };

  const addRule = () => {
    const usedKeys = new Set(sort.map((rule) => rule.key));
    const nextOption = options.find((option) => !usedKeys.has(option.key));
    if (nextOption) onSortChange([...sort, { key: nextOption.key, direction: 'asc' }]);
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm" className={cn('gap-2', className)}>
          <ArrowUpDown className="h-4 w-4" />
          Sort
          {sort.length > 0 && <Badge variant="secondary">{sort.length}</Badge>}
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Advanced sorting</DialogTitle>
          <DialogDescription>
            Sort rules are applied from top to bottom. Later rules break ties from earlier ones.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {sort.length === 0 ? (
            <div className="rounded-[13px] border border-dashed border-border bg-muted/40 px-4 py-6 text-center text-sm text-muted-foreground">
              No sort rules applied. Items remain in their default order.
            </div>
          ) : (
            sort.map((rule, index) => {
              const usedKeys = new Set(sort.map((item) => item.key));
              const availableOptions = options.filter(
                (option) => option.key === rule.key || !usedKeys.has(option.key)
              );

              return (
                <div
                  key={`${rule.key}-${index}`}
                  className="grid grid-cols-[auto_minmax(0,1fr)] gap-2 rounded-[13px] border border-border bg-muted/40 p-3 sm:grid-cols-[auto_minmax(0,1fr)_9rem_auto] sm:items-center"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-[10px] bg-background text-xs font-bold text-muted-foreground">
                    {index + 1}
                  </span>

                  <Select
                    value={rule.key}
                    onValueChange={(key) => updateRule(index, { key: key as TKey })}
                  >
                    <SelectTrigger aria-label={`Sort rule ${index + 1} field`} className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availableOptions.map((option) => (
                        <SelectItem key={option.key} value={option.key}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select
                    value={rule.direction}
                    onValueChange={(direction) =>
                      updateRule(index, { direction: direction as 'asc' | 'desc' })
                    }
                  >
                    <SelectTrigger
                      aria-label={`Sort rule ${index + 1} direction`}
                      className="col-span-2 h-9 sm:col-span-1"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="asc">Ascending</SelectItem>
                      <SelectItem value="desc">Descending</SelectItem>
                    </SelectContent>
                  </Select>

                  <div className="col-span-2 flex items-center justify-end gap-1 sm:col-span-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => moveRule(index, -1)}
                      disabled={index === 0}
                      aria-label={`Move sort rule ${index + 1} up`}
                      title="Move up"
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => moveRule(index, 1)}
                      disabled={index === sort.length - 1}
                      aria-label={`Move sort rule ${index + 1} down`}
                      title="Move down"
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                      onClick={() => removeRule(index)}
                      aria-label={`Remove sort rule ${index + 1}`}
                      title="Remove rule"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })
          )}

          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full gap-2 sm:w-auto"
            onClick={addRule}
            disabled={sort.length >= options.length}
          >
            <Plus className="h-4 w-4" />
            Add sort rule
          </Button>
        </div>

        <DialogFooter className="gap-2 sm:space-x-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => onSortChange([])}
            disabled={sort.length === 0}
          >
            Clear sorting
          </Button>
          <DialogClose asChild>
            <Button type="button">Done</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ListSortMenu;
